// web/src/components/charts/RetentionChart.jsx
import React, { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { normalizeTimestamp } from "../../utils/dateUtils";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

function RetentionChart() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  // ✅ Janelas corretas (em ms)
  const MS_24H = 24 * 60 * 60 * 1000;
  const MS_7D = 7 * 24 * 60 * 60 * 1000;

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "users"),
      (snap) => {
        const now = Date.now();
        let dau = 0;
        let wau = 0;

        snap.forEach((doc) => {
          const user = doc.data();

          let lastActive = normalizeTimestamp(user.lastActiveAt);
          if (!lastActive) lastActive = normalizeTimestamp(user.lastActive);
          if (!lastActive) return;

          const diffMs = now - lastActive.getTime();

          if (diffMs <= MS_24H) dau++;
          if (diffMs <= MS_7D) wau++;
        });

        setData([
          { name: "DAU", value: dau },
          { name: "WAU", value: wau },
        ]);

        setLoading(false);
      },
      (err) => {
        console.error("Erro ao carregar retenção (DAU/WAU):", err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  const dau = data.find((d) => d.name === "DAU")?.value ?? 0;
  const wau = data.find((d) => d.name === "WAU")?.value ?? 0;

  const dauWauPct = useMemo(() => {
    if (!wau) return 0;
    return Math.round((dau / wau) * 100);
  }, [dau, wau]);

  if (loading) {
    return (
      <div className="chartBox">
        <h3>Retenção (DAU / WAU) 🔁</h3>
        <div className="chart-loading">Carregando gráfico...</div>
      </div>
    );
  }

  return (
    <div className="chartBox">
      <h3>
        Retenção (DAU / WAU) 🔁{" "}
        <span style={{ fontSize: 12, fontWeight: 500, opacity: 0.7 }}>
          — {dauWauPct}% (DAU/WAU)
        </span>
      </h3>

      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <CartesianGrid stroke="#ffe5ef" strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis allowDecimals={false} />
          <Tooltip
            formatter={(value) => [`${value}`, "Usuários ativos"]}
            contentStyle={{
              backgroundColor: "white",
              border: "1px solid #ff4f9a",
              borderRadius: "8px",
              fontSize: "12px",
              padding: "8px",
            }}
          />
          <Bar dataKey="value" fill="#ff4f9a" radius={[8, 8, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>

      {/* ✅ Resumo numérico */}
      <div
        style={{
          marginTop: 10,
          fontSize: 12,
          color: "#666",
          display: "flex",
          gap: 12,
          justifyContent: "center",
          flexWrap: "wrap",
        }}
      >
        <span>
          DAU: <strong>{dau}</strong>
        </span>
        <span>
          WAU: <strong>{wau}</strong>
        </span>
        <span>
          Retenção: <strong>{dauWauPct}%</strong>
        </span>
      </div>

      {/* ✅ DESCRIÇÃO EXPLICATIVA (NOVO) */}
      <div
        style={{
          marginTop: 14,
          paddingTop: 10,
          borderTop: "1px dashed #ffd6ea",
          fontSize: 12,
          color: "#777",
          lineHeight: 1.5,
          textAlign: "center",
        }}
      >
        <strong>DAU (Daily Active Users)</strong> representa os usuários ativos
        nas últimas <strong>24 horas</strong>. <br />
        <strong>WAU (Weekly Active Users)</strong> indica os usuários ativos nos
        últimos <strong>7 dias</strong>. <br />
        A taxa <strong>DAU/WAU</strong> mostra o nível de engajamento diário da
        base semanal de usuários.
      </div>
    </div>
  );
}

export default RetentionChart;
